import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { CartAndLineItemGroups } from '@/types/commercetools';
import { isCurrentPromoActivatedAtom, promoConfigAtom } from '@/atoms/promoAtom';
import { Assortment } from '@/utility/shopIds';
import { useCart } from './useCartHooks';
import { useIsMultiCouponEnabled } from './useIsMultiCouponEnabled';
import { useStrikePrice } from './useStrikePrice';
const filterLineItemGroups = (cartAndLineItemGroups: CartAndLineItemGroups, productTypeIds: string[]) => {
  return cartAndLineItemGroups.lineItemGroups.filter(group => group.productTypeId && productTypeIds.includes(String(group.productTypeId)));
};

/**
 * The item's price with coupon discounts (`price` includes its volume discount). The multi-coupon
 * variant asks pricing-service for all active coupons (`useStrikePrice`); the control applies the
 * activated CMS campaign's `PercentageBased` conditions.
 */
export const useDiscountedPrice = (price, productTypeId, t0) => {
  const $ = _c(16);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    hasVolumeDiscount: t2,
    assortment: t3
  } = t1;
  const hasVolumeDiscount = t2 === undefined ? false : t2;
  const assortment = t3 === undefined ? "marketplace" : t3;
  const isMultiCouponEnabled = useIsMultiCouponEnabled();
  let t4;
  if ($[2] !== assortment || $[3] !== hasVolumeDiscount || $[4] !== price || $[5] !== productTypeId) {
    t4 = {
      price,
      productTypeId,
      hasVolumeDiscount,
      assortment
    };
    $[2] = assortment;
    $[3] = hasVolumeDiscount;
    $[4] = price;
    $[5] = productTypeId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const strikePrice = useStrikePrice(t4);
  const promoConfig = useAtomValue(promoConfigAtom);
  const isPromoActivated = useAtomValue(isCurrentPromoActivatedAtom);
  const {
    data: cartAndLineItemGroups
  } = useCart();
  let t5;
  bb0: {
    if (!productTypeId || !promoConfig || !promoConfig.content["promo-tool"].elements.strikethroughPrices || !isPromoActivated) {
      t5 = null;
      break bb0;
    }
    const t6 = promoConfig.content["promo-tool"];
    let t7;
    if ($[7] !== cartAndLineItemGroups || $[8] !== price || $[9] !== productTypeId || $[10] !== t6.promoConditions) {
      let t8;
      if ($[12] !== cartAndLineItemGroups || $[13] !== price || $[14] !== productTypeId) {
        t8 = condition => {
          if (condition.restrictions.productTypes && !condition.restrictions.productTypes.includes(productTypeId)) {
            return false;
          }
          if (condition.restrictions.minOrderItems) {
            const basketItemCount = !cartAndLineItemGroups ? 0 : condition.restrictions.restrictByCategoryOrProductTypes ? filterLineItemGroups(cartAndLineItemGroups, condition.restrictions.productTypes || []).reduce(_temp2, 0) : cartAndLineItemGroups.cart.totalLineItemQuantity;
            if (basketItemCount + 1 < condition.restrictions.minOrderItems) {
              return false;
            }
          }
          if (condition.restrictions.minOrderValue) {
            const basketValue = !cartAndLineItemGroups ? 0 : condition.restrictions.restrictByCategoryOrProductTypes ? filterLineItemGroups(cartAndLineItemGroups, condition.restrictions.productTypes || []).reduce(_temp3, 0) : cartAndLineItemGroups.lineItemGroups.reduce(_temp4, 0);
            if (basketValue / 100 + price < condition.restrictions.minOrderValue) {
              return false;
            }
          }
          return true;
        };
        $[12] = cartAndLineItemGroups;
        $[13] = price;
        $[14] = productTypeId;
        $[15] = t8;
      } else {
        t8 = $[15];
      }
      t7 = t6.promoConditions.filter(_temp).find(t8);
      $[7] = cartAndLineItemGroups;
      $[8] = price;
      $[9] = productTypeId;
      $[10] = t6.promoConditions;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    t5 = t7;
  }
  const matchingCondition = t5;
  if (isMultiCouponEnabled) {
    return strikePrice ?? price;
  }
  if (matchingCondition) {
    return price - price * (matchingCondition.value / 100);
  }
  return price;
};
function _temp(c) {
  return c.type === "PercentageBased";
}
function _temp2(sum, group) {
  return sum + group.groupQuantity;
}
function _temp3(sum_0, group_0) {
  return sum_0 + group_0.groupTotalCentAmount;
}
function _temp4(sum_1, group_1) {
  return sum_1 + group_1.groupTotalCentAmount;
}